import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './_styles_/HolidayYearCreateHoliday.css';
import { holidayDatesType } from '../../types';
import TextField from '../fields/TextField';
import useExtendedState from '../../hooks/useExtendedState';
import useSingleValidator from '../../hooks/useSingleValidator';
import {
    HOLIDAYS_NAME_MAX_LENGTH,
    holidaysDateValidator,
    holidaysNameValidator,
} from '../../validators/holidays';
import DateField, { DAY_AND_MONTH } from '../fields/DateField';
import {
    toDateWithoutTime,
    toLocalDate,
} from '../../utils';
import { compositeValidator, wafValidator } from '../../hooks/validationSupport';

HolidayYearCreateHoliday.propTypes = {
    holidays: holidayDatesType.isRequired,
    onHolidayYearDateCreate: PropTypes.func.isRequired,
    year: PropTypes.number,
};

HolidayYearCreateHoliday.defaultProps = {
    year: null,
};

function HolidayYearCreateHoliday(props) {
    const {
        holidays,
        year,
        onHolidayYearDateCreate,
    } = props;
    const { t } = useTranslation();
    const nameState = useExtendedState(null);
    const holidayDateState = useExtendedState(null);
    const {
        isValid: isNameValid,
        validator: nameValidator,
    } = useSingleValidator(false, useMemo(() => compositeValidator(holidaysNameValidator(t), wafValidator(t)), [t]));
    const {
        isValid: isDateValid,
        validator: dateValidator,
    } = useSingleValidator(false, useMemo(() => holidaysDateValidator(t, holidays), [t, holidays]));
    const [isAddButtonDisabled, setIsAddButtonDisabled] = useState(true);
    Promise.all([isNameValid(), isDateValid()])
        .then(() => setIsAddButtonDisabled(false))
        .catch(() => setIsAddButtonDisabled(true));

    const addHolidayDate = () => {
        onHolidayYearDateCreate(nameState.value, toLocalDate(holidayDateState.value).substring(5));
        nameState.reset();
        holidayDateState.reset();
    };
    const initialViewDate = toDateWithoutTime(holidayDateState.value);
    if (year) {
        initialViewDate.setFullYear(year);
    }
    const isValidDate = (currentDate) => currentDate.year() === year;
    return (
        <div className="holiday-year--add-holiday">
            <TextField
                label={t('holidays.holiday.name')}
                state={nameState}
                className="holiday-year--fields--name"
                inputId="holiday-year--fields--name"
                validator={nameValidator}
                maxLength={HOLIDAYS_NAME_MAX_LENGTH}
            />
            <DateField
                label={t('holidays.holiday.date')}
                state={holidayDateState}
                className="holiday-year--fields--date"
                inputId="holiday-year--fields--date"
                validator={dateValidator}
                dateFormat={DAY_AND_MONTH}
                isValidDate={isValidDate}
                initialViewDate={initialViewDate}
            />
            <button
                type="button"
                className="add-holiday-date-button button button-bordered"
                disabled={isAddButtonDisabled}
                onClick={addHolidayDate}
            >
                <FontAwesomeIcon icon="plus" />&nbsp;{t('holidays.add-holiday-date')}
            </button>
        </div>
    );
}

export default HolidayYearCreateHoliday;
